import { useCallback, useEffect, useState } from "react";
import {
  CalendarOff,
  CalendarX2,
  FileClock,
  Inbox,
  PencilLine,
  Users,
} from "lucide-react";
import PageContainer from "../../components/PageContainer";
import { PageHeader } from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { competenciaAtual } from "./pontoUtils";
import CartoesTab from "./CartoesTab";
import MarcacoesTab from "./MarcacoesTab";
import JornadasTab from "./JornadasTab";
import DiasFechadosTab from "./DiasFechadosTab";
import AfastamentosTab from "./AfastamentosTab";
import SolicitacoesTab from "./SolicitacoesTab";

const ABAS = [
  { chave: "cartoes", rotulo: "Cartão ponto", icone: FileClock },
  { chave: "solicitacoes", rotulo: "Solicitações", icone: Inbox },
  { chave: "ajustes", rotulo: "Ajustes de marcação", icone: PencilLine },
  {
    chave: "afastamentos",
    rotulo: "Férias e afastamentos",
    icone: CalendarOff,
  },
  { chave: "feriados", rotulo: "Dias fechados", icone: CalendarX2 },
  { chave: "jornadas", rotulo: "Colaboradoras", icone: Users },
];

const ABA_SALVA_KEY = "gestaoPontoAba";

const lerAbaSalva = () => {
  try {
    const aba = localStorage.getItem(ABA_SALVA_KEY);
    return ABAS.some((item) => item.chave === aba) ? aba : "cartoes";
  } catch {
    return "cartoes";
  }
};

/** Área da gestão: cartão ponto calculado, ajustes e cadastros do ponto. */
const GestaoPonto = () => {
  const { showError } = useNotification();
  const [aba, setAba] = useState(lerAbaSalva);
  const [colaboradoras, setColaboradoras] = useState([]);
  const [pendentes, setPendentes] = useState(0);
  // Filtro compartilhado entre "Cartão ponto" e "Ajustes", para ir de uma pendência ao ajuste.
  const [filtro, setFiltro] = useState({
    competencia: competenciaAtual(),
    usuarioId: "",
  });

  const carregarColaboradoras = useCallback(async () => {
    try {
      setColaboradoras(await pontoService.listarColaboradoras());
    } catch (erro) {
      showError(erro.message);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregarColaboradoras();
  }, [carregarColaboradoras]);

  const carregarPendentes = useCallback(async () => {
    try {
      setPendentes(await pontoService.contarSolicitacoesPendentes());
    } catch {
      setPendentes(0);
    }
  }, []);

  useEffect(() => {
    carregarPendentes();
  }, [carregarPendentes]);

  const trocarAba = (chave) => {
    setAba(chave);
    try {
      localStorage.setItem(ABA_SALVA_KEY, chave);
    } catch {
      // Preferência opcional.
    }
  };

  const abrirAjustes = (usuarioId, competencia) => {
    setFiltro({ usuarioId, competencia });
    trocarAba("ajustes");
  };

  const comJornada = colaboradoras.filter((item) => item.jornada);

  return (
    <PageContainer className="p-4 sm:p-6">
      <PageHeader
        title="Gestão do ponto"
        description="Cartão ponto das colaboradoras, ajustes e cadastros. Só a gestão vê esta área."
      />

      <div
        role="tablist"
        className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-gray-200 bg-white p-1"
      >
        {ABAS.map(({ chave, rotulo, icone: Icone }) => (
          <button
            key={chave}
            role="tab"
            type="button"
            aria-selected={aba === chave}
            onClick={() => trocarAba(chave)}
            className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              aba === chave
                ? "bg-blue-600 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <Icone size={16} />
            {rotulo}
            {chave === "solicitacoes" && pendentes > 0 && (
              <span
                className={`rounded-full px-1.5 text-xs font-semibold ${
                  aba === chave
                    ? "bg-white text-blue-700"
                    : "bg-red-600 text-white"
                }`}
              >
                {pendentes}
              </span>
            )}
          </button>
        ))}
      </div>

      {aba === "cartoes" && (
        <CartoesTab
          colaboradoras={comJornada}
          filtro={filtro}
          onFiltro={setFiltro}
          onAbrirAjustes={abrirAjustes}
        />
      )}
      {aba === "solicitacoes" && (
        <SolicitacoesTab onAlterado={carregarPendentes} />
      )}
      {aba === "ajustes" && (
        <MarcacoesTab
          colaboradoras={comJornada}
          filtro={filtro}
          onFiltro={setFiltro}
        />
      )}
      {aba === "afastamentos" && <AfastamentosTab colaboradoras={comJornada} />}
      {aba === "feriados" && <DiasFechadosTab />}
      {aba === "jornadas" && (
        <JornadasTab
          colaboradoras={colaboradoras}
          onAtualizado={carregarColaboradoras}
        />
      )}
    </PageContainer>
  );
};

export default GestaoPonto;
